import React, { useState, useRef } from "react";
import { Marker } from "react-leaflet";
import ReactDOM from "react-dom";
import L from "leaflet";
import { FaCube } from "react-icons/fa";
import ReactDOMServer from "react-dom/server";
import Ply3DTooltip from "./Ply3DTooltip";
import "../styles/ply3DMarkers.css";

// Define the PLY file locations with their coordinates
// Coordinates are in decimal degrees format
const PLY_LOCATIONS = [
  {
    id: 1,
    name: "01.ply",
    lat: 44.4235,
    lng: 8.88558,
    description: "Point Cloud 1",
  },
  {
    id: 2,
    name: "02.ply",
    lat: 44.42219,
    lng: 8.88439,
    description: "Point Cloud 2",
  },
  {
    id: 3,
    name: "03.ply",
    lat: 44.42422,
    lng: 8.88244,
    description: "Point Cloud 3",
  },
  {
    id: 4,
    name: "04.ply",
    lat: 44.42842,
    lng: 8.88303,
    description: "Point Cloud 4",
  },
  {
    id: 5,
    name: "05.ply",
    lat: 44.43244,
    lng: 8.88594,
    description: "Point Cloud 5",
  },
  {
    id: 6,
    name: "06.ply",
    lat: 44.43533,
    lng: 8.8865,
    description: "Point Cloud 6",
  },
  {
    id: 7,
    name: "07.ply",
    lat: 44.43611,
    lng: 8.88689,
    description: "Point Cloud 7",
  },
  {
    id: 8,
    name: "08.ply",
    lat: 44.4375,
    lng: 8.88758,
    description: "Point Cloud 8",
  },
  {
    id: 9,
    name: "09.ply",
    lat: 44.44047,
    lng: 8.88833,
    description: "Point Cloud 9",
  },
  {
    id: 10,
    name: "10.ply",
    lat: 44.44633,
    lng: 8.88944,
    description: "Point Cloud 10",
  },
  {
    id: 11,
    name: "11.ply",
    lat: 44.44361,
    lng: 8.88933,
    description: "Point Cloud 11",
  },
  {
    id: 12,
    name: "12.ply",
    lat: 44.43575,
    lng: 8.88911,
    description: "Point Cloud 12",
  },
  {
    id: 13,
    name: "13.ply",
    lat: 44.42994,
    lng: 8.88953,
    description: "Point Cloud 13",
  },
  {
    id: 14,
    name: "14.ply",
    lat: 44.42478,
    lng: 8.88653,
    description: "Point Cloud 14",
  },
  {
    id: 15,
    name: "15.ply",
    lat: 44.42308,
    lng: 8.88439,
    description: "Point Cloud 15",
  },
  {
    id: 16,
    name: "16.ply",
    lat: 44.42336,
    lng: 8.88503,
    description: "Point Cloud 16",
  },
  {
    id: 17,
    name: "17.ply",
    lat: 44.42425,
    lng: 8.88483,
    description: "Point Cloud 17",
  },
  {
    id: 18,
    name: "18.ply",
    lat: 44.42819,
    lng: 8.8835,
    description: "Point Cloud 18",
  },
  {
    id: 19,
    name: "19.ply",
    lat: 44.42728,
    lng: 8.88558,
    description: "Point Cloud 19",
  },
  {
    id: 20,
    name: "20.ply",
    lat: 44.42656,
    lng: 8.88756,
    description: "Point Cloud 20",
  },
  {
    id: 21,
    name: "21.ply",
    lat: 44.42472,
    lng: 8.88636,
    description: "Point Cloud 21",
  },
];

// Helper function to convert DMS to decimal degrees
function dmsToDecimal(degrees, minutes, seconds, direction) {
  let decimal = degrees + minutes / 60 + seconds / 3600;
  if (direction === "S" || direction === "W") {
    decimal = -decimal;
  }
  return decimal;
}

// Create custom 3D icon
const create3DIcon = () => {
  const iconHtml = ReactDOMServer.renderToString(
    <div
      style={{
        width: "32px",
        height: "32px",
        backgroundColor: "#A1E340",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      }}
    >
      <FaCube style={{ color: "white", fontSize: "18px" }} />
    </div>
  );

  return L.divIcon({
    html: iconHtml,
    className: "ply-3d-marker",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
};

// Ply3DMarkers component
function Ply3DMarkers({ onMarkerClick, showMarkers = true }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [tooltipPortal, setTooltipPortal] = useState(null);
  const icon = create3DIcon();

  // Create portal container on mount
  React.useEffect(() => {
    const portalDiv = document.createElement("div");
    portalDiv.id = "ply-tooltip-portal";
    document.body.appendChild(portalDiv);
    setTooltipPortal(portalDiv);

    return () => {
      document.body.removeChild(portalDiv);
    };
  }, []);

  // Close popup when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectedLocation &&
        !event.target.closest(".ply-3d-tooltip") &&
        !event.target.closest(".ply-3d-marker")
      ) {
        setSelectedLocation(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [selectedLocation]);

  if (!showMarkers) {
    return null;
  }

  const handleMarkerClick = (location, event) => {
    if (event && event.containerPoint) {
      // Get the map container position
      const mapContainer = event.target._map.getContainer();
      const mapRect = mapContainer.getBoundingClientRect();

      // Calculate absolute position
      setPopupPosition({
        x: mapRect.left + event.containerPoint.x,
        y: mapRect.top + event.containerPoint.y,
      });
      setSelectedLocation(location);
    }
  };

  return (
    <>
      {PLY_LOCATIONS.map((location) => (
        <Marker
          key={location.id}
          position={[location.lat, location.lng]}
          icon={icon}
          eventHandlers={{
            click: (e) => {
              e.originalEvent.stopPropagation();
              handleMarkerClick(location, e);
            },
          }}
        ></Marker>
      ))}

      {/* Render popup in portal */}
      {tooltipPortal &&
        selectedLocation &&
        ReactDOM.createPortal(
          <Ply3DTooltip
            location={selectedLocation}
            onView3D={(loc) => {
              setSelectedLocation(null);
              onMarkerClick(loc);
            }}
            onClose={() => setSelectedLocation(null)}
            visible={true}
            position={popupPosition}
          />,
          tooltipPortal
        )}
    </>
  );
}

export { PLY_LOCATIONS };
export default Ply3DMarkers;
